import _ from 'lodash'
import config from 'scoreboard/components/config'
import escapePreserveLineBreaks from 'scoreboard/components/escape-preserve-line-breaks'
import formatValue from 'scoreboard/components/format-value'
import i18n from 'scoreboard/components/i18n'
import momentHelper from 'scoreboard/components/moment-helper'
import replicatedMessage from 'scoreboard/components/replicated-message'
import groupType from 'scoreboard/admin/principals/groups/group-type'
import DataType from 'scoreboard/datasets/model/data-type'
const { NUMBER, CURRENCY, DATE } = DataType
import addHrefToPrincipals from 'scoreboard/components/add-href-to-principals'
/**
 * Generates a context object that contains all available attributes
 * ready for display.
 *
 * @param {Backbone.Model} model The Initiative Model
 * @param {boolean} [opts.replaceWithUnshortened=false] if true shortened values
 * will be replaced with the unshortened value.
 */
function context(model, opts={}) {

	var {replaceWithUnshortened} = opts

	var m = _modelGet.bind(model)

	var type = m('type')
	var typeLabel = type && i18n.get('initiatives.type.' + type)
	var goalFunction = m('goalFunction')
	var goalFunctionLabel = _.isEmpty(goalFunction) ? undefined :
							i18n.get('initiatives.goalFunctions.' + _.camelCase(goalFunction))
	var pctElapsed = formatValue({ value: m('percentTimeElapsed'), dataType: 'percent'})

	//Currencies
	var currencyCode = m('currencyCode')
	var currencyHash = { dataType: CURRENCY, currencyCode: currencyCode }
	var totalBudgetHash = _.extend({value: m('aggregatedBudget')}, currencyHash)
	var totalBudget = formatValue(_.extend(totalBudgetHash))
	var budgetRemainingHash = _.extend({value: m('budgetRemaining')}, currencyHash)
	var budgetRemaining = formatValue(budgetRemainingHash)
	var budgetVarianceHash = _.extend({value: m('budgetVariance')}, currencyHash)
	var budgetVariance = formatValue(budgetVarianceHash)

	//Projected Budget Attributes
	var projectedMsgCode = m('projectedBudget','descriptiveMessageCode')

	var absolutePercentUnder = formatValue(
		{ dataType: 'percent', value: m('projectedBudget','absolutePercentUnder')})

	var absoluteAmountUnderHash = _.extend({value: m('projectedBudget','absoluteAmountUnder')}, currencyHash)
	var absoluteAmountUnder = formatValue(absoluteAmountUnderHash)

	var projectedTotalHash = _.extend({value: m('projectedBudget','total')}, currencyHash)
	var projectedTotal = formatValue(projectedTotalHash)

	let scheduleVariance = formatValue({value: m('scheduleVariance')})

	//Detail Section
	var hash = {
		'common.type': typeLabel,
		'common.currency': currencyCode,
		'initiatives.goalFunction': goalFunctionLabel,
		'initiatives.budget': totalBudget,
		'initiatives.budgetRemaining' : budgetRemaining,
		'initiatives.calculation.projectedBudgetVariance': budgetVariance,
		'initiatives.calculation.projectedScheduleVariance': scheduleVariance,
		'initiatives.startDate': m('aggregatedStartDate'),
		'initiatives.dueDate': m('aggregatedEndDate'),
		'initiatives.completionDate': m('aggregatedCompletionDate'),
		'initiatives.percentTimeElapsed': pctElapsed,
		'initiatives.projectedEndDate': m('projectedSchedule', 'projectedEndDate'),
		'initiatives.projectedSchedule': m('projectedSchedule', 'descriptiveMessage'),
		'initiatives.projectedTotal': projectedTotal,
		//These keys are not used for display, but must be distinct inside the hash
		'initiatives.projected.Pct': absolutePercentUnder,
		'initiatives.projected.Amt': absoluteAmountUnder,
		'common.replication.replicated': replicatedMessage(model)
	}
	const shortenFormatOpts = {shorten: false}
	// The overview must show the unshortened values and omit the shortened ones
	const unshortenedSuffix = replaceWithUnshortened ? '' : 'Unshortened'
	const resolveValue = (shortened, unshortened) => {
		if (shortened == unshortened) {
			// Must return null so the tooltips don't show if values are equal
			return replaceWithUnshortened ? unshortened : null
		}
		return unshortened
	}
	hash[`initiatives.budget${unshortenedSuffix}`] = resolveValue(totalBudget, formatValue(_.extend(totalBudgetHash, shortenFormatOpts)))
	hash[`initiatives.budgetRemaining${unshortenedSuffix}`] = resolveValue(budgetRemaining, formatValue(_.extend(budgetRemainingHash, shortenFormatOpts)))
	hash[`initiatives.projectedTotal${unshortenedSuffix}`] = resolveValue(projectedTotal, formatValue(_.extend(projectedTotalHash, shortenFormatOpts)))
	hash[`initiatives.projected.Pct${unshortenedSuffix}`] = resolveValue(absolutePercentUnder, formatValue(_.extend({dataType: 'percent', value: m('projectedBudget','absolutePercentUnder')}, shortenFormatOpts)))
	hash[`initiatives.projected.Amt${unshortenedSuffix}`] = resolveValue(absoluteAmountUnder, formatValue(_.extend(absoluteAmountUnderHash, shortenFormatOpts)))
	hash[`initiatives.calculation.projectedBudgetVariance${unshortenedSuffix}`] = resolveValue(budgetVariance, formatValue(_.extend(budgetVarianceHash, shortenFormatOpts)))
	hash[`initiatives.calculation.projectedScheduleVariance${unshortenedSuffix}`] = resolveValue(scheduleVariance, formatValue(shortenFormatOpts))

	var availableDetails = _.reduce(hash, function(acc, value, key) {
		if (!_.isEmpty(value) || _.isNumber(value) || _.isBoolean(value)) {
			var isDynamicCode = _.startsWith(key,'initiatives.projected.')
			var k = isDynamicCode ? projectedMsgCode : key
			acc.push({key: k, value: value})
		}
		return acc
	}, [])

	//Explicit collections with their own section
	var assignedUsersAndGroups = addHrefToPrincipals(groupType
		.groupTypeLabelAssignTo(m('assignees')))
	var expenditures = m('expenditures')
	_.each(expenditures, function(expenditure) {
		var amountHash = _.extend({value : expenditure.amount}, currencyHash)
		,	amount = formatValue(amountHash)
		expenditure.amount = amount
		const amountUnshortened = formatValue(_.extend(amountHash, shortenFormatOpts))
		expenditure[`amount${unshortenedSuffix}`] = resolveValue(amount, amountUnshortened)
	})

	// Here we're distilling some of the models values into two vars which
	// make building the template more simple
	var hasBasicInfo = false
	var hasSchedule = false

	if (m('aggregatedStartDate') || m('aggregatedEndDate') || m('aggregatedCompletionDate')) {
		hasSchedule = true
		hasBasicInfo = true
	} else if (m('description') || totalBudget || m('budgetRemaining')) {
		hasBasicInfo = true
	}

	// Warning icons and tooltips for "budget remaining", "start date", and "due date"
	let budgetWarning, startDateWarning, endDateWarning

	_.forEachRight(m('history'), h => { //start at the end, already sorted by date in ascending order
		// #23547 don't check for another budget change if we've already made a
		// message for the most recent one
		if (!budgetWarning && h.field === 'budget') {
			var currencyOptions = {
				dataType: CURRENCY,
				currencyCode: currencyCode,
				shorten: false
			}

			if (h.newValue && h.oldValue) {
				var oldValue = formatValue(_.extend(currencyOptions, { value: h.oldValue }))
				var newValue = formatValue(_.extend(currencyOptions, { value: h.newValue }))
				budgetWarning = i18n.get('initiatives.changeToFrom.tooltip', momentHelper.fromNow(h.date), `<b>${_.escape(h.user.label)}</b>`, `<b>${_.escape(oldValue)}</b>`, `<b>${_.escape(newValue)}</b>`)
			} else if (h.newValue) {
				var newValue = formatValue(_.extend(currencyOptions, { value: h.newValue }))
				budgetWarning = i18n.get('initiatives.changeTo.tooltip', momentHelper.fromNow(h.date), `<b>${_.escape(h.user.label)}</b>`, `<b>${_.escape(newValue)}</b>`)
			}
		}

		endDateWarning = buildDateChangeWarning(h, 'endDate', endDateWarning)
		startDateWarning = buildDateChangeWarning(h, 'startDate', startDateWarning)

		// continue until we exhaust the list or find one of each
		return !budgetWarning || !endDateWarning || !startDateWarning
	})

	//Put it all together and ship it!
	return {
		id: config.displayIds ? m('id') : null,
		initiativeTypeLabel: i18n.get('common.info.withArg', typeLabel),
		label: m('label'),
		description: escapePreserveLineBreaks(m('description')),
		details: availableDetails,
		expenditures, //props: id, date, label, amount
		assignedUsersAndGroups,
		notes: m('notes'),
		history: m('history'),
		hasSchedule,
		hasBasicInfo,
		budgetWarning,
		startDateWarning,
		endDateWarning,
		tags: m('tags'),
		/**
		 * Get an initiative detail value by its key
		 * @param key
		 * @returns {string}
		 */
		value: function(key) {
			return hash[key]
		},
		/**
		 * Get an array of key/value objects for all keys provided.
		 * Only non-empty values that exist will be returned
		 *
		 * @param keys
		 * @returns {array}
		 */
		pick: function(keys) {
			return _.reduce(availableDetails, (acc, obj) => {
				if (_.includes(keys, obj.key)) {
					acc.push(obj)
				}
				return acc
			},[])
		},

		/**
		 * @param key
		 * @returns {string} the unshortened value for the given key if any.
		 * If no unshortened value was defind the the shortened value for the key.
		 * otherwise null
		 */
		valueUnshortened (key) {
			return hash[key+'Unshortened'] || hash[key] || null
		},

		/**
		 * If a value needs to be added to the context that was not derived from the
		 * common set of model attributes.
		 *
		 * @param key should be an i18n key
		 * @param value the value to be displayed
		 */
		pushDetail: function(key,value) {
			availableDetails.push({key:key, value:value})
		},

		/**
		 * A model for this initiative suitable for building an excel export row
		 */
		exportContext() {
			const useDomValue = undefined
			const fields = [
				useDomValue, // Name
				{ value: model.get('id'), dataType: NUMBER },
				useDomValue, // Type
				useDomValue, // Description
				useDomValue, // Related Items
				useDomValue, // Org
				useDomValue, // Tags
				useDomValue, // Assigned
				_.extend({value: model.get('budgetSpent')}, currencyHash),
				totalBudgetHash,
				{ value: model.get('aggregatedStartDate'), dataType: DATE},
				{ value: model.get('aggregatedEndDate'), dataType: DATE},
				{ value: model.get('aggregatedCompletionDate'), dataType: DATE},
				useDomValue // Is Archived?
			]
			return fields
		}
	}
}

function buildDateChangeWarning(history, dateField, existingWarning) {
	// #23547 don't check for another start/due date change if we've already made a
	// message for the most recent one
	if (!existingWarning && history.field === dateField) {
		// Dates in oldValue/newValue should be in ISO-8601 format
		if (history.newValue && history.oldValue) {
			return i18n.get('initiatives.changeToFrom.tooltip',
				momentHelper.fromNow(history.date),
				`<b>${_.escape(history.user.label)}</b>`,
				`<b>${momentHelper.formatDate(history.oldValue)}</b>`,
				`<b>${momentHelper.formatDate(history.newValue)}</b>`)
		} else if (history.newValue) {
			return i18n.get('initiatives.changeTo.tooltip',
				momentHelper.fromNow(history.date),
				`<b>${_.escape(history.user.label)}</b>`,
				`<b>${momentHelper.formatDate(history.newValue)}</b>`)
		}
	}

	return existingWarning
}

/**
 * Get an attribute from the model, of which this function is bound to.
 * A date attribute will be converted automatically
 *
 * @param attribute
 * @param nestedExpression passed to _.result(attribute, nestedExpression)
 * @returns {undefined} if the value is empty, the actual value otherwise
 * @private
 */
function _modelGet(attribute, nestedExpression) {
	var a =  this.get(attribute)
	if (nestedExpression) {
		a = _.result(a, nestedExpression)
	}
	var value = undefined
	if (_.isDate(a) ||
		_.endsWith(_.lowerCase(attribute), DATE) ||
		_.endsWith(_.lowerCase(nestedExpression),DATE)
	) {
		value = momentHelper.formatDate(a)
	} else if (_.isNumber(a) || _.isBoolean(a) || !_.isEmpty(a)) {
		value = a
	}
	return value
}


export default context

context.__filename__ = 'initiatives/overview/initiatives-context.js'
